/* eslint-disable @typescript-eslint/indent */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useContext, useMemo, useState } from 'react';
import Currency from '@components/common/Currrency/Currency';
import Product from '@customTypes/product';
import { timeSelected } from '@events/timeSlottedProduct';
import { isProductUnAvailable as isUnAvailable } from '@helpers/product';
import { parseMilitaryTime } from '@helpers/date';
import { CartItemDetails } from '@customTypes/cart-items';
import HotelDetails from '@customTypes/hotel-details';
import { store } from '@context/store';
import actionTypes from '@context/actionTypes';
import useHotSpot from '@hooks/useHotSpot';
import { FAIR_PRICING_VARIANT } from '@constants/fairPricing';
import { getProductPriceForGuestType } from '@utils/fairPricing';
import {
  AMPLITUDE_EXPERIMENT_CONTROL_NAME,
  AMPLITUDE_EXPERIMENT_VARIANT_NAME,
  AMPLITUDE_EXPERIMENT_VARIANT_NAME_2,
} from '@constants/amplitudeExperiments';
import DiscountPriceWithSTP from '@components/STP/ProductDetails/DiscountPriceWithSTP';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';
import cn from '@helpers/className';
import TimeSlotGuestSelectorDrawer from './TimeSlotGuestSelectorDrawer';

type Props = {
  cartData: CartItemDetails | undefined;
  date: Date | undefined;
  hotelDetails: HotelDetails;
  parentProduct: Product;
  product: Product;
  showFairPricing: boolean;
};

function TimeSlotItem({
  cartData,
  date,
  hotelDetails,
  parentProduct,
  product,
  showFairPricing,
}: Props) {
  const [isBottomDrawerOpen, setIsBottomDrawerOpen] = useState<boolean>(false);

  const { applyHotSpotDiscountToTotal, isDateWithinVisitWindow } = useHotSpot();
  const { ctasUpdateExperiment } = useFeatureFlagContext();
  const { dispatch, state } = useContext(store);
  const { pricingMarkdownExperiment } = state;

  const isProductUnAvailable = isUnAvailable(product, cartData);

  const isTimeSelected = useMemo(
    () => product.product_id.toString() === state.selectedTimeSlot?.product_id.toString(),
    [state.selectedTimeSlot?.product_id, product.product_id],
  );

  const isPricingExperiment = useMemo(
    () => pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME,
    [pricingMarkdownExperiment],
  );

  const handleSelectTime = () => {
    if (isProductUnAvailable) return;

    dispatch({
      type: actionTypes.SET_SELECTED_TIME_SLOT,
      payload: { ...parentProduct, ...product },
    });

    // Event
    timeSelected(product);
  };

  return (
    <div
      role="button"
      key={product.product_id.toString()}
      onClick={handleSelectTime}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          handleSelectTime();
        }
      }}
      tabIndex={0}
      className={`flex flex-col no-select-highlight ${isProductUnAvailable ? 'opacity-40' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div
          className={`flex ${
            isPricingExperiment ? 'flex-row w-full items-center justify-between pr-6' : 'flex-col'
          }`}
        >
          <p
            className={`${
              isPricingExperiment ? 'font-rp-pn-regular' : 'font-rp-pn-semi-bold'
            } leading-6 text-18`}
          >
            {`${parseMilitaryTime(product.start_time)} - ${parseMilitaryTime(product.end_time)}`}
          </p>

          <div
            className={`flex ${
              isPricingExperiment ? 'flex-col items-end' : 'items-center'
            }  font-rp-pn-regular  text-custom-black`}
          >
            {date &&
            product.is_hot_spot &&
            hotelDetails.hot_spot &&
            isDateWithinVisitWindow(date, hotelDetails) ? (
              <div className="mr-2">
                <Currency
                  value={applyHotSpotDiscountToTotal(
                    +product.adult_price,
                    hotelDetails.hot_spot,
                    showFairPricing,
                  )}
                  valueClasses="leading-4 text-solar-brand text-md"
                />
              </div>
            ) : null}

            {pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? (
              <DiscountPriceWithSTP
                cartData={cartData}
                date={date}
                hotelDetails={hotelDetails}
                personType="adult"
                product={product}
                showFairPricing={showFairPricing}
                isTimeSlotted
                hideDiscountPrice={product.individual_billing}
              />
            ) : (
              <Currency
                value={getProductPriceForGuestType(product, 'adult', showFairPricing)}
                valueClasses={`leading-4 ${
                  date && product.is_hot_spot && isDateWithinVisitWindow(date, hotelDetails)
                    ? 'text-rp-disabled text-base line-through'
                    : 'text-custom-black text-md'
                }`}
              />
            )}
            {isPricingExperiment &&
            !product.individual_billing &&
            !(date && isUnAvailable(product, cartData, hotelDetails, date)) &&
            showFairPricing ? (
              <span className="text-sm text-rp-gray whitespace-nowrap">
                {FAIR_PRICING_VARIANT === 'variant1'
                  ? 'Includes fees'
                  : `+$${(+getProductPriceForGuestType(product, 'adult', showFairPricing)).toFixed(
                      0,
                    )} in fees`}
              </span>
            ) : null}
          </div>

          {!isPricingExperiment &&
          !(date && isUnAvailable(product, cartData, hotelDetails, date)) &&
          showFairPricing ? (
            <span className="text-sm text-rp-gray whitespace-nowrap">
              {FAIR_PRICING_VARIANT === 'variant1'
                ? 'Includes fees'
                : `+$${(+getProductPriceForGuestType(product, 'adult', showFairPricing)).toFixed(
                    0,
                  )} in fees`}
            </span>
          ) : null}
        </div>

        <div className="flex items-center relative">
          <label
            className="flex items-center justify-center"
            htmlFor={`product_${product.product_id.toString()}`}
          >
            <span
              className={cn({
                'cursor-pointer h-6 right-5 d:right-6 rounded-full top-6 w-6': true,
                'border border-rp-gray-border-3': !isTimeSelected,
                'border-7': isTimeSelected,
                'border-rp-primary-dark':
                  isTimeSelected &&
                  (!ctasUpdateExperiment ||
                    ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_CONTROL_NAME),
                'border-solar-surface-brand-shadow':
                  isTimeSelected && ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME,
                'border-solar-ultramarine':
                  isTimeSelected && ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME_2,
              })}
            />

            <input
              id={`product_${product.product_id.toString()}`}
              className="hidden"
              type="radio"
              name={product.product_id.toString()}
              value={product.product_id.toString()}
              checked={
                product.product_id.toString() === state.selectedTimeSlot?.product_id.toString()
              }
              disabled={isProductUnAvailable}
              onChange={handleSelectTime}
            />
          </label>
        </div>
      </div>

      {isBottomDrawerOpen && (
        <TimeSlotGuestSelectorDrawer
          showFairPricing={showFairPricing}
          cartData={cartData}
          date={date}
          hotelDetails={hotelDetails}
          isOpen={isBottomDrawerOpen}
          product={product}
          setIsOpen={setIsBottomDrawerOpen}
        />
      )}
    </div>
  );
}

export default TimeSlotItem;
